/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { type ReactNode } from 'react';

import { jsx } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading/heading';
import { ConfluenceIcon, ConfluenceLogo } from '@atlaskit/logo';
import { Box, Grid, Stack, Text } from '@atlaskit/primitives/compiled';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { Hide } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import type { SVGComponent } from '../../types';

type LayoutProps = {
	children: ReactNode;
	headerLayoutSpacing: SpacingToken;
	hideLogo?: boolean;
	logoVariant?: LogoVariant;
	profileRow?: ReactNode;
	screen: string;
	spacingLayout: SpacingToken;
	subtitle?: ReactNode;
	subtitleSize?: 'small' | 'medium' | 'large';
	title: string;
};

type LogoVariant = 'full' | 'icon';

const styles = cssMap({
	fullWidth: { width: '100%' },
	dynamicComponentEdgeToEdge: {
		width: '100%',
		height: '100%',
		overflow: 'hidden',
		position: 'relative',
	},
	imageContainer: {
		display: 'none',

		'@media (min-width: 48rem)': {
			overflow: 'hidden',
			display: 'block',
		},
	},
	form: {
		paddingTop: token('space.600'),
		paddingRight: token('space.600'),
		paddingBottom: token('space.600'),
		paddingLeft: token('space.600'),
	},
	grid: {
		gridTemplateColumns: '1fr',
		height: '100%',
		'@media (min-width: 48rem)': {
			gridTemplateColumns: '1fr 1fr',
		},
	},
	leftSection: {
		minHeight: '0px',
		overflowY: 'auto',
	},
	headingContainer: {
		paddingBottom: token('space.100'),
	},
});

const stackSpacing = {
	100: 'space.100',
	150: 'space.150',
	200: 'space.200',
	250: 'space.250',
	300: 'space.300',
	400: 'space.400',
} as const;

type SpacingToken = keyof typeof stackSpacing;

export const SplitScreen = ({
	applyUpdatedLayout = false,
	title,
	subtitle,
	subtitleSize,
	screen,
	imageComponent: Image,
	dynamicComponent,
	isDynamicComponentEdgeToEdge = false,
	imageAlt,
	footer,
	children,
	hideLogo = false,
	logoVariant = 'full',
	profileRow,
	spacingOverall = 400,
	spacingLayout = 400,
	headerLayoutSpacing = 200,
}: {
	applyUpdatedLayout?: boolean;
	children: ReactNode;
	dynamicComponent?: ReactNode;
	footer: ReactNode;
	headerLayoutSpacing?: SpacingToken;
	hideLogo?: boolean;
	imageAlt: string;
	imageComponent?: SVGComponent;
	isDynamicComponentEdgeToEdge?: boolean;
	logoVariant?: LogoVariant;
	profileRow?: ReactNode;
	screen: string;
	spacingLayout?: SpacingToken;
	spacingOverall?: SpacingToken;
	subtitle?: ReactNode;
	subtitleSize?: 'small' | 'medium' | 'large';
	title: string;
}): JSX.Element => {
	const layoutProps = {
		title,
		subtitle,
		subtitleSize,
		screen,
		children,
		hideLogo,
		logoVariant,
		profileRow,
		spacingLayout,
		headerLayoutSpacing,
	};
	const shouldApplyAriaHidden = FeatureGates.getExperimentValue(
		'cc_activation_a11y_fixes',
		'isEnabled',
		false,
	);

	return (
		<Grid xcss={styles.grid}>
			<Stack
				grow="fill"
				spread="space-between"
				space={stackSpacing[spacingOverall]}
				xcss={cx(styles.form, styles.leftSection)}
			>
				{applyUpdatedLayout ? <UpdatedLayout {...layoutProps} /> : <OldLayout {...layoutProps} />}
				{footer}
			</Stack>
			<Hide below="sm" xcss={styles.imageContainer}>
				{Image && (
					<Image
						viewBox="0 0 565 665"
						title={imageAlt}
						aria-label={imageAlt}
						preserveAspectRatio="xMinYMid slice"
						width="100%"
						height="100%"
						{...(!imageAlt && { 'aria-hidden': true, role: 'presentation' })}
					/>
				)}
				{!!dynamicComponent &&
					!Image &&
					(isDynamicComponentEdgeToEdge ? (
						<Box
							testId="split-screen-dynamic-component-wrapper"
							xcss={styles.dynamicComponentEdgeToEdge}
						>
							{dynamicComponent}
						</Box>
					) : shouldApplyAriaHidden ? (
						<Box
							aria-hidden="true"
							testId="split-screen-dynamic-component-wrapper"
							xcss={styles.dynamicComponentEdgeToEdge}
						>
							{dynamicComponent}
						</Box>
					) : (
						dynamicComponent
					))}
			</Hide>
		</Grid>
	);
};

const OldLayout = ({
	title,
	subtitle,
	subtitleSize = 'medium',
	screen,
	children,
	hideLogo,
	logoVariant,
	profileRow,
	spacingLayout,
	headerLayoutSpacing,
}: LayoutProps) => (
	<>
		<Stack alignInline="start" space={stackSpacing[spacingLayout]}>
			{!!profileRow && profileRow}
			{!hideLogo &&
				(logoVariant === 'icon' ? (
					<ConfluenceIcon appearance="brand" shouldUseNewLogoDesign />
				) : (
					<ConfluenceLogo appearance="brand" />
				))}
			<Stack alignInline="start" space={hideLogo ? stackSpacing[headerLayoutSpacing] : 'space.100'}>
				<Heading size="xlarge" testId={`${screen}-heading`} id={`${screen}-heading`}>
					{title}
				</Heading>
				{subtitle && (
					<Text size={subtitleSize} color="color.text.subtle">
						{subtitle}
					</Text>
				)}
			</Stack>
			<Box xcss={styles.fullWidth}>{children}</Box>
		</Stack>
	</>
);

const UpdatedLayout = ({
	title,
	subtitle,
	subtitleSize = 'medium',
	screen,
	children,
	hideLogo,
	logoVariant,
	profileRow,
	spacingLayout,
	headerLayoutSpacing,
}: LayoutProps) => (
	<>
		<Stack alignInline="start" space={stackSpacing[spacingLayout]}>
			{!!profileRow && profileRow}
			{!hideLogo &&
				(logoVariant === 'icon' ? (
					<ConfluenceIcon appearance="brand" shouldUseNewLogoDesign />
				) : (
					<ConfluenceLogo appearance="brand" shouldUseNewLogoDesign />
				))}
			<Stack alignInline="start" space="space.100">
				<Stack
					space={hideLogo ? stackSpacing[headerLayoutSpacing] : 'space.100'}
					xcss={styles.headingContainer}
				>
					<Heading size="xlarge" testId={`${screen}-heading`} id={`${screen}-heading`}>
						{title}
					</Heading>
					{subtitle && (
						<Text size={subtitleSize} color="color.text.subtle">
							{subtitle}
						</Text>
					)}
				</Stack>
				<Box xcss={styles.fullWidth}>{children}</Box>
			</Stack>
		</Stack>
	</>
);
